// frontend/src/lib/httpClient.ts
// ✅ COMPLETELY FIXED: Proper handling of public vs protected routes with TypeScript fixes

import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api';
const REQUEST_TIMEOUT = 30000;

// Extend the request config to include _retry flag
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Token management
class TokenManager {
  private static instance: TokenManager;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  private constructor() {
    this.loadTokensFromStorage();
  }

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  private loadTokensFromStorage(): void {
    // Try to load from new key first, then fall back to legacy keys for migration
    this.accessToken = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || localStorage.getItem('token');
    this.refreshToken = localStorage.getItem('refresh_token');
    
    // If we found tokens with legacy keys, migrate them
    const legacyToken = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (legacyToken && !localStorage.getItem('auth_token')) {
      localStorage.setItem('auth_token', legacyToken);
      localStorage.removeItem('access_token');
      localStorage.removeItem('token');
    }
  }

  getToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  setTokens(accessToken: string, refreshToken?: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken || null;
    
    // Use consistent key and clear any legacy keys
    localStorage.setItem('auth_token', accessToken);
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
  }

  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    
    // Clear all possible token keys (current and legacy)
    localStorage.removeItem('auth_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  // ✅ NEW: Check if a route is public (should never trigger token refresh)
  isPublicRoute(url: string): boolean {
    const publicRoutes = [
      '/auth/login',
      '/auth/register',
      '/auth/register/customer',
      '/auth/register/partner',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/verify-email',
      '/auth/resend-verification',
      '/auth/check-email',
      '/auth/social/login',
      '/auth/social/register',
      '/auth/refresh', // Refresh is special - it's public but uses refresh token
    ];

    return publicRoutes.some(route => url.includes(route));
  }

  // ✅ NEW: Check if we should attempt token refresh on 401
  shouldAttemptRefresh(url: string): boolean {
    // Never attempt refresh for public routes
    if (this.isPublicRoute(url)) {
      return false;
    }

    // Only attempt if we have a refresh token
    if (!this.refreshToken) {
      return false;
    }

    // Don't attempt if we're already refreshing
    return true;
  }

  async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) {
      console.log('No refresh token available');
      return false;
    }

    try {
      // Use a clean axios instance to avoid interceptor loops
      const refreshClient = axios.create({
        baseURL: API_BASE_URL,
        timeout: REQUEST_TIMEOUT,
      });

      console.log('Attempting to refresh token...');
      const response = await refreshClient.post('/auth/refresh', {
        refresh_token: this.refreshToken
      });

      if (response.data?.success && response.data?.data?.token) {
        console.log('Token refresh successful');
        this.setTokens(response.data.data.token, response.data.data.refresh_token || this.refreshToken);
        return true;
      }

      console.log('Token refresh failed - invalid response format');
      this.clearTokens();
      return false;
    } catch (error: any) {
      console.log('Token refresh failed:', error?.response?.data || error.message);
      
      // Only clear tokens if it's a client error (400-499), not server error (500+)
      if (error?.response?.status >= 400 && error?.response?.status < 500) {
        this.clearTokens();
      }
      return false;
    }
  }

  redirectToLogin(): void {
    // Only redirect if not already on auth pages
    const currentPath = window.location.pathname;
    const authPaths = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];
    
    if (!authPaths.some(path => currentPath.startsWith(path))) {
      window.location.href = '/login';
    }
  }
}

// Create HTTP client instance
const createHttpClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: REQUEST_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const tokenManager = TokenManager.getInstance();

  // Request interceptor - Add authentication token ONLY for protected routes
  client.interceptors.request.use(
    (config) => {
      // ✅ CRITICAL FIX: Check if route is public first
      const url = config.url || '';
      
      // Skip auth for explicitly marked requests
      if (config.headers?.skipAuth) {
        delete config.headers.skipAuth;
        return config;
      }

      // ✅ NEW: Skip auth for public routes automatically
      if (tokenManager.isPublicRoute(url)) {
        return config;
      }

      // Add token only for protected routes
      const token = tokenManager.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Handle FormData
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // ✅ COMPLETELY REWRITTEN: Response interceptor with proper route handling and TypeScript fixes
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;
      
      // ✅ FIX: Handle undefined originalRequest
      if (!originalRequest) {
        return Promise.reject(error);
      }

      const requestUrl = originalRequest.url || '';

      // Handle 401 errors
      if (error.response?.status === 401) {
        
        // ✅ CRITICAL: For public routes, NEVER attempt refresh or redirect
        if (tokenManager.isPublicRoute(requestUrl)) {
          console.log('401 on public route - letting form handle it:', requestUrl);
          return Promise.reject(error);
        }

        // ✅ For protected routes, attempt token refresh ONCE
        if (tokenManager.shouldAttemptRefresh(requestUrl) && !originalRequest._retry) {
          originalRequest._retry = true;

          console.log('401 error on protected route, attempting token refresh:', requestUrl);
          const refreshSuccess = await tokenManager.refreshAccessToken();
          
          if (refreshSuccess) {
            console.log('Token refresh successful, retrying original request');
            // Update the original request with new token
            const newToken = tokenManager.getToken();
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            
            // Retry the original request
            return client(originalRequest);
          } else {
            console.log('Token refresh failed, cannot retry request');
          }
        }

        // ✅ Only redirect for protected routes after refresh fails
        if (!tokenManager.isPublicRoute(requestUrl)) {
          console.log('Token refresh failed, redirecting to login');
          tokenManager.clearTokens();
          tokenManager.redirectToLogin();
        }
      }

      // ✅ FIX: Handle undefined error.response safely
      if (error.response && error.response.status >= 500) {
        console.error('Server error:', error.response.data);
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Export the HTTP client and token manager
export const httpClient = createHttpClient();
export { TokenManager };
export const tokenManager = TokenManager.getInstance();

// Export types
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: Record<string, string[]>;
  error_code?: number;
  error_type?: string;
  timestamp?: string;
}